import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Upload, Download } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const UOMS = ["each", "box", "bag", "pallet", "linear_ft", "sq_ft", "cubic_yd", "ton", "bundle", "roll", "gallon", "hour", "lot"];

const EMPTY = {
  name: "", sku: "", manufacturer_part_number: "", upc: "", brand: "", manufacturer: "",
  description: "", category: "", subcategory: "", tags: "", images: "", uom: "each",
  package_qty: "", weight: "", moq: 1, order_increment: 1, special_order: false,
  lead_time_days: "", price: "", price_uom: "", stock_status: "in_stock", stock_qty: "",
  coverage_value: "", coverage_unit: "sq_ft", coverage_depth_in: "", active: true,
  // Package 39 — long-tail record (workflow doc)
  price_effective_date: "", price_expiry_date: "", ships_from: "",
  delivery_method: "", freight_class: "", hazmat: false, pallet_configuration: "",
};

function toPayload(form) {
  return {
    name: form.name, sku: form.sku || null, manufacturer_part_number: form.manufacturer_part_number || null,
    upc: form.upc || null, brand: form.brand || null, manufacturer: form.manufacturer || null,
    description: form.description || null, category: form.category || null, subcategory: form.subcategory || null,
    tags: (form.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
    images: (form.images || "").split("\n").map((u) => u.trim()).filter(Boolean),
    uom: form.uom, package_qty: form.package_qty ? Number(form.package_qty) : null,
    weight: form.weight ? Number(form.weight) : null,
    coverage_rate: form.coverage_value
      ? { value: Number(form.coverage_value), unit: form.coverage_unit, per_unit: form.uom,
          ...(form.coverage_depth_in ? { depth_in: Number(form.coverage_depth_in) } : {}) }
      : null,
    moq: Number(form.moq) || 1, order_increment: Number(form.order_increment) || 1,
    special_order: !!form.special_order, lead_time_days: form.lead_time_days ? Number(form.lead_time_days) : null,
    price: Number(form.price), price_uom: form.price_uom || null,
    stock_status: form.stock_status, stock_qty: form.stock_qty ? Number(form.stock_qty) : null,
    price_effective_date: form.price_effective_date || null,
    price_expiry_date: form.price_expiry_date || null,
    ships_from: form.ships_from || null,
    delivery_method: form.delivery_method || null,
    freight_class: form.freight_class || null,
    hazmat: !!form.hazmat,
    pallet_configuration: form.pallet_configuration || null,
    active: form.active !== false,
  };
}

function fromProduct(p) {
  return {
    ...EMPTY, ...p,
    tags: (p.tags || []).join(", "),
    images: (p.images || []).join("\n"),
    package_qty: p.package_qty ?? "", weight: p.weight ?? "", lead_time_days: p.lead_time_days ?? "",
    stock_qty: p.stock_qty ?? "", price_uom: p.price_uom ?? "",
    coverage_value: p.coverage_rate?.value ?? "", coverage_unit: p.coverage_rate?.unit ?? "sq_ft",
    coverage_depth_in: p.coverage_rate?.depth_in ?? "",
    price_effective_date: p.price_effective_date ?? "", price_expiry_date: p.price_expiry_date ?? "",
    ships_from: p.ships_from ?? "", delivery_method: p.delivery_method ?? "",
    freight_class: p.freight_class ?? "", hazmat: !!p.hazmat,
    pallet_configuration: p.pallet_configuration ?? "",
  };
}

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/vendor/catalog").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShow(true); };
  const openEdit = (p) => { setEditing(p); setForm(fromProduct(p)); setShow(true); };

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Name and price required");
    try {
      if (editing) await api.patch(`/vendor/catalog/${editing.id}`, toPayload(form));
      else await api.post("/vendor/catalog", toPayload(form));
      toast.success(editing ? "Product updated" : "Product added");
      setShow(false);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save product");
    }
  };

  const remove = async (id) => {
    await api.delete(`/vendor/catalog/${id}`);
    load();
  };

  const downloadTemplate = async () => {
    const res = await api.get("/vendor/catalog/import/template", { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "catalog-template.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/vendor/catalog/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.errors.length) {
        toast.warning(`Imported ${data.imported}, updated ${data.updated} — ${data.errors.length} row error(s): ` +
          data.errors.slice(0, 3).map((er) => `row ${er.row}: ${er.message}`).join("; "));
      } else {
        toast.success(`Imported ${data.imported} new, updated ${data.updated}`);
      }
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const F = ({ label, field, type = "text", placeholder = "", className = "" }) => (
    <div className={className}>
      <label className="text-[11px] font-semibold text-slate-500">{label}</label>
      <input type={type} value={form[field]} onChange={(e) => setForm({ ...form, [field]: e.target.value })} placeholder={placeholder}
        data-testid={`product-${field}-input`} className="mt-0.5 w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px]" />
    </div>
  );

  return (
    <div className="p-6 md:p-8" data-testid="vendor-catalog-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">My Catalog</h1>
          <p className="text-slate-500 text-sm mt-1">Manage products and pricing visible to contractors.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadTemplate} data-testid="csv-template-button" className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50">
            <Download size={13} /> CSV Template
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="csv-import-button" className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50 disabled:opacity-50">
            <Upload size={13} /> {importing ? "Importing…" : "Import CSV / Excel"}
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={importCsv} className="hidden" />
          <button onClick={openNew} data-testid="add-product-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
            <Plus size={14} /> Add Product
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full mt-7 text-[13px]" data-testid="vendor-catalog-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2.5">Product</th><th className="text-left py-2.5">SKU</th><th className="text-right py-2.5">Price</th><th className="text-right py-2.5">MOQ</th><th className="text-left py-2.5 pl-3">Stock</th><th></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-slate-50" data-testid={`product-row-${p.id}`}>
                <td className="py-3">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-[11.5px] text-slate-400">{[p.brand, p.category, p.subcategory].filter(Boolean).join(" · ")}</div>
                </td>
                <td className="py-3 mono text-slate-500">{p.sku || "—"}</td>
                <td className="py-3 text-right mono font-medium">{fmt(p.price)}<span className="text-slate-400 text-[11px]">/{p.uom}</span></td>
                <td className="py-3 text-right mono text-slate-500">{p.moq > 1 ? p.moq : "—"}</td>
                <td className="py-3 pl-3"><StatusBadge status={p.stock_status} /></td>
                <td className="py-3 text-right flex gap-2 justify-end">
                  <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil size={14} className="text-slate-400 hover:text-sky-600" /></button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-product-${p.id}`}><Trash2 size={14} className="text-slate-400 hover:text-red-500" /></button>
                </td>
              </tr>
            ))}
            {products.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400 text-sm">No products yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="product-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <F label="Name *" field="name" className="col-span-2" />
            <F label="SKU" field="sku" /><F label="Manufacturer Part #" field="manufacturer_part_number" />
            <F label="UPC" field="upc" /><F label="Brand" field="brand" />
            <F label="Manufacturer" field="manufacturer" /><F label="Category" field="category" />
            <F label="Subcategory" field="subcategory" /><F label="Tags (comma-separated)" field="tags" />
            <div className="col-span-2">
              <label className="text-[11px] font-semibold text-slate-500">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
                data-testid="product-description-input" className="mt-0.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]" />
            </div>
            <div className="col-span-2">
              <label className="text-[11px] font-semibold text-slate-500">Image URLs (one per line, first = primary)</label>
              <textarea value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} rows={2}
                data-testid="product-images-input" className="mt-0.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] mono" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Unit of Measure</label>
              <SearchableSelect value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} data-testid="product-uom-select" className="mt-0.5 w-full h-9 px-2 rounded-lg border border-slate-200 text-[13px]">
                {UOMS.map((u) => <option key={u} value={u}>{u}</option>)}
              </SearchableSelect>
            </div>
            <F label="Price *" field="price" type="number" />
            <F label="Package Qty" field="package_qty" type="number" /><F label="Weight (lb)" field="weight" type="number" />
            <F label="MOQ" field="moq" type="number" /><F label="Order Increment" field="order_increment" type="number" />
            <F label="Lead Time (days)" field="lead_time_days" type="number" /><F label="Stock Qty" field="stock_qty" type="number" />
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Stock Status</label>
              <SearchableSelect value={form.stock_status} onChange={(e) => setForm({ ...form, stock_status: e.target.value })} data-testid="product-stock-select" className="mt-0.5 w-full h-9 px-2 rounded-lg border border-slate-200 text-[13px]">
                <option value="in_stock">In Stock</option><option value="low">Low Stock</option><option value="out_of_stock">Out of Stock</option>
              </SearchableSelect>
            </div>
            <label className="flex items-center gap-2 text-[12.5px] text-slate-600 mt-5">
              <input type="checkbox" checked={form.special_order} onChange={(e) => setForm({ ...form, special_order: e.target.checked })} /> Special order
            </label>
            <div className="col-span-2 border-t border-slate-100 pt-3">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Pricing validity &amp; logistics</div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Price Effective Date" field="price_effective_date" type="date" />
                <F label="Price Expiry Date" field="price_expiry_date" type="date" />
                <F label="Ships From" field="ships_from" placeholder="e.g. Branch 402 — Springfield" />
                <div>
                  <label className="text-[11px] font-semibold text-slate-500">Delivery Method</label>
                  <SearchableSelect value={form.delivery_method} onChange={(e) => setForm({ ...form, delivery_method: e.target.value })} data-testid="product-delivery-select" className="mt-0.5 w-full h-9 px-2 rounded-lg border border-slate-200 text-[13px]">
                    <option value="">—</option><option value="vendor_delivers">Vendor delivers</option><option value="pickup_only">Pickup only</option><option value="freight">Freight</option>
                  </SearchableSelect>
                </div>
                <F label="Freight Class (NMFC)" field="freight_class" placeholder="e.g. 55" />
                <F label="Pallet Configuration" field="pallet_configuration" placeholder="e.g. 48 cases/pallet" />
              </div>
              <label className="flex items-center gap-2 text-[12.5px] text-slate-600 mt-2.5">
                <input type="checkbox" checked={form.hazmat} onChange={(e) => setForm({ ...form, hazmat: e.target.checked })} data-testid="product-hazmat-checkbox" /> Hazmat — ships with hazardous-materials handling
              </label>
            </div>
            <div className="col-span-2 border-t border-slate-100 pt-3">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Coverage (optional — enables the calculator)</div>
              <div className="grid grid-cols-3 gap-3">
                <F label={`Covers (per ${form.uom})`} field="coverage_value" type="number" placeholder="e.g. 8" />
                <div>
                  <label className="text-[11px] font-semibold text-slate-500">Coverage Unit</label>
                  <SearchableSelect value={form.coverage_unit} onChange={(e) => setForm({ ...form, coverage_unit: e.target.value })} className="mt-0.5 w-full h-9 px-2 rounded-lg border border-slate-200 text-[13px]">
                    <option value="sq_ft">sq_ft</option><option value="linear_ft">linear_ft</option><option value="cubic_yd">cubic_yd</option>
                  </SearchableSelect>
                </div>
                <F label="At Depth (in)" field="coverage_depth_in" type="number" placeholder="e.g. 3" />
              </div>
            </div>
            <button onClick={save} data-testid="save-product-button" className="col-span-2 h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold mt-1">
              {editing ? "Save Changes" : "Add Product"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
